// react
import { MouseEvent, useContext, useState } from 'react';

// router
import { useSearchParams } from 'react-router-dom';

// types
import { User, UserProject } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextUsersPermissions } from '../../../pages/Project/Project.context';

// mui
import { Button } from '@mui/material';

// dialogs
import { DialogUpdateUserPermissions } from '../../../dialogs/DialogUpdateUserPermissions/DialogUpdateUserPermissions';

// ui
import { Table, TableHeader, TableProps } from '../../../ui/Table/Table';
import { UserAvatar } from '../../../ui/UserAvatar/UserAvatar';

// links
import { LinkEmail } from '../../../links/LinkEmail/LinkEmail';

// icons
import { DeleteOutline as IconDelete } from '@mui/icons-material';
import { IconCircle, IconCircleProps } from '../../../ui/IconCircle/IconCircle';

// props
export interface TableBasicUsersPermissionsProps extends Omit<TableProps, 'headers'> {
	data: User[];
	project?: number;
}

export const TableBasicUsersPermissions = ({
	data = [],
	project,
	...props
}: TableBasicUsersPermissionsProps) => {
	// hooks
	const { t } = useTranslation();

	// params
	const [searchParams, setSearchParams] = useSearchParams();
	const paramDialog = searchParams.get('dialog');

	// context
	const { refetchUsersPermissions } = useContext(ContextUsersPermissions);

	// state
	const [activeUser, setActiveUser] = useState<User | null>(null);

	const headers = [
		{
			label: t('user'),
			value: 'email',
			render: (email: string, header: TableHeader, user: User) => {
				return <UserAvatar user={user} />;
			},
		},
		{
			label: t('email'),
			render: (email: string, header: TableHeader, user: User) => {
				return <LinkEmail email={email} />;
			},
			value: 'email',
		},
		{
			label: t('beneficiaries'),
			value: 'projects',
			render: (projects: UserProject[]) => {
				const IconCircleProps: IconCircleProps = {
					color: 'grey',
					type: 'minus',
				};

				const userProjectPermissions = projects.find((p) => p.id === project)?.permissions;

				if (project && userProjectPermissions?.beneficiary?.readPii) {
					IconCircleProps.color = 'success';
					IconCircleProps.type = 'check';
				}

				return <IconCircle size="xs" {...IconCircleProps} />;
			},
		},
		{
			label: '',
			value: 'firstName',
			render: (firstName: string, header: TableHeader, user: User) => {
				return (
					<Button
						aria-label={t('removePermissions', { name: firstName })}
						className="icon"
						color="error"
						onClick={(e: MouseEvent<HTMLButtonElement>) => {
							searchParams.set('dialog', 'updateUserPermissions');
							setSearchParams(searchParams);
							setActiveUser(user);
						}}
						variant="outlined">
						<IconDelete />
					</Button>
				);
			},
		},
	];

	return (
		<>
			<Table {...props} data={data} headers={headers} />
			{activeUser && (
				<DialogUpdateUserPermissions
					isOpen={paramDialog === 'updateUserPermissions'}
					onClose={() => {
						searchParams.delete('dialog');
						setSearchParams(searchParams);
					}}
					onSuccess={() => {
						refetchUsersPermissions();
					}}
					user={activeUser as User}
				/>
			)}
		</>
	);
};
